import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, Avatar, Space, Typography, Tag } from 'antd';
import {
  DashboardOutlined,
  DesktopOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  SendOutlined,
  FileTextOutlined,
  CloudServerOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, logout } = useAuthStore();

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  const selectedKeys = [location.pathname === '/' ? '/' : '/' + location.pathname.split('/')[1]];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} width={220}>
        <div className="logo" style={{ margin: '16px', textAlign: 'center' }}>
          <Text strong style={{ color: 'white', fontSize: collapsed ? 16 : 20 }}>
            LLCP
          </Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          onClick={handleMenuClick}
          items={[
            {
              key: '/',
              icon: <DashboardOutlined />,
              label: '仪表盘',
            },
            {
              key: '/servers',
              icon: <DesktopOutlined />,
              label: '服务器管理',
            },
            // RBAC 菜单门禁：管理员管理仅 admin 可见；命令测试仅 admin/operator 可见
            ...(role === 'admin'
              ? [{ key: '/admins', icon: <TeamOutlined />, label: '管理员管理' }]
              : []),
            ...(role === 'admin'
              ? [{ key: '/mqtt-subscriptions', icon: <CloudServerOutlined />, label: 'MQTT 订阅管理' }]
              : []),
            ...(role === 'admin'
              ? [{ key: '/mqtt-servers', icon: <CloudServerOutlined />, label: 'MQTT 服务器管理' }]
              : []),
            ...(role !== 'viewer'
              ? [{ key: '/command-test', icon: <SendOutlined />, label: '命令测试' }]
              : []),
            {
              key: '/api-logs',
              icon: <FileTextOutlined />,
              label: 'API日志',
            },
            { key: '/rpm-build', icon: <FileTextOutlined />, label: 'RPM 构建' },
            { key: '/deb-build', icon: <FileTextOutlined />, label: 'DEB 构建' },
            { key: '/iso-build', icon: <FileTextOutlined />, label: 'ISO 构建' },
            ...(role === 'admin' ? [{ key: '/build-management', icon: <CloudServerOutlined />, label: '构建管理' }] : []),
            // 个人中心：所有登录用户可见（查看个人信息 + 修改密码）
            {
              key: '/profile',
              icon: <UserOutlined />,
              label: '个人中心',
            },
          ]}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: 0, background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleCollapsed}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />
          <div style={{ marginRight: 20 }}>
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }}>
              <Space>
                <Avatar icon={<UserOutlined />} />
                {user?.realName || user?.username || '用户'}
                <Tag color={role === 'admin' ? 'gold' : role === 'operator' ? 'blue' : 'default'} style={{ marginRight: 0 }}>
                  {role || 'viewer'}
                </Tag>
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
